import { Visibility } from '@mui/icons-material';
import { Grid, IconButton, useEventCallback } from '@mui/material';
import { memo, ReactElement, useEffect, useMemo, useState } from 'react';
import Table from 'shell/components/shared/table';
import { headCells } from './constants';
import { noop } from 'shell/utils/noop';
import { NotificationsList } from 'civil-status/view-models/notifications-list';
import { CriteriRicercaNotifiche } from 'civil-status/model/notifications';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { getValueStateNotification } from 'civil-status/constants/states-notifications';
import { getNameTypeContent } from 'civil-status/constants/id-type-content';
import { getNameTypeEvent } from 'civil-status/utils/get-name-type-event';
import { HttpResponse } from 'shell/models/http-response';
import { ResponsePreview } from 'civil-status/view-models/step-form';
import { AxiosResponse } from 'axios';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import ModalPrintSuccess from 'practice/components/detail/form/print-comunication/modal';

interface TableActProps {
  data?: Array<NotificationsList>;
  filterSelected?: CriteriRicercaNotifiche;
  getNotificationsListData: (
    data?: CriteriRicercaNotifiche,
    page?: number,
    rowsPerPage?: number
  ) => void;
  tableProps?: Record<string, number>;
  setTableProps: (values: Record<string, number>) => void;
  idCat?: string;
  nameCat?: string;
  setTablePropsFilterTable: (page?: number, rowsPerPage?: number) => void;
}

const TableActComponent = ({
  data,
  filterSelected,
  getNotificationsListData,
  tableProps = {},
  setTableProps,
  setTablePropsFilterTable,
}: TableActProps): ReactElement => {
  // stato per stampa anteprima
  const [pdfModal, setPdfModal] = useState({
    data: '',
    open: false,
    name: '',
    item: {},
  });

  // ModalPrintSuccess
  const [modalPrintSuccess, setModalPrintSuccess] = useState<{
    open: boolean;
    total?: number;
  }>({
    open: false,
  });

  const [dataList, setDataList] = useState<Array<NotificationsList> | undefined>();
  const [itemToModal, setItemToModal] = useState<{ item?: NotificationsList; isConfirm: boolean }>({
    item: undefined,
    isConfirm: false,
  });

  useEffect(() => {
    setDataList(data);
  }, [data]);

  const titleModal: string = useMemo(() => {
    if (itemToModal.isConfirm) {
      return 'ACCETTAZIONE NOTIFICA';
    }

    return 'RIFIUTO NOTIFICA';
  }, [itemToModal.item]);

  const messageModal: string = useMemo(() => {
    return `Hai selezionato l'ID evento ${itemToModal.item?.id || ''} con intestatario ${
      itemToModal.item?.holders || ''
    }. Una volta ${
      itemToModal.isConfirm ? 'confermata' : 'rifiutata'
    } la notifica la scelta non potrà essere modificata. Sei sicuro di voler procedere?`;
  }, [itemToModal.item]);

  // old detail press
  // const onDetailPress = useEventCallback((item: NotificationsList) => {
  //   if (!(item.idAnsc && item.id && item.stateCode)) {
  //     return;
  //   }
  //   if (idCat && nameCat) {
  //     navigate({
  //       pathname: generatePath(
  //         '/stato-civile/notifiche/ansc/ricerca/:valueCat/:idCat/visualizza/:id/:idNotification/:stateNotification',
  //         {
  //           id: item.idAnsc,
  //           valueCat: nameCat,
  //           idCat: idCat,
  //           idNotification: item.id,
  //           stateNotification: item.stateCode,
  //         }
  //       ),
  //     });

  //     return;
  //   }

  //   navigate({
  //     pathname: generatePath(
  //       '/stato-civile/notifiche/ansc/ricerca/visualizza/:id/:idNotification/:stateNotification',
  //       {
  //         id: item.idAnsc,
  //         idNotification: item.id,
  //         stateNotification: item.stateCode,
  //       }
  //     ),
  //   });
  // });

  const onPreview = useHttpClient('', {
    options: {
      method: 'GET',
      loading: true,
      errorCatching: true,
    },
  });

  const onDetailPress = useEventCallback((item: NotificationsList) => {
    if (!item.idAnsc) {
      return;
    }

    (
      onPreview({
        url: `${__ACT_API_URL__}/evento/anteprima/idAnsc/${item.idAnsc}`,
      }) as unknown as ResponsePromise<HttpResponse<ResponsePreview>, HttpResponse<ResponsePreview>>
    ).then((response) => {
      toast.success('Operazione eseguita con successo');

      const responseCasted = response as AxiosResponse<HttpResponse<ResponsePreview>>;

      const pdf = responseCasted.data.payload.eventoOutput?.contenuto;
      if (pdf) {
        setPdfModal({
          data: pdf,
          name: 'Anteprima atto',
          open: true,
          item,
        });
      }
    });
  });

  const onConfirm = useHttpClient(`${__ACT_API_URL__}/notifiche/conferma`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const onYesConfirm = useEventCallback((item?: NotificationsList) => {
    if (!item?.id) {
      return;
    }

    const dataConfirm = {
      idNotifica: item?.id || undefined,
    };

    (
      onConfirm({
        data: dataConfirm,
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      setItemToModal({ item: undefined, isConfirm: false });
      // ricaricare la tabella
      getNotificationsListData(filterSelected || {}, tableProps.page, tableProps.rowsPerPage);
    });
  });

  const onRejection = useHttpClient(`${__ACT_API_URL__}/notifiche/rifiuta`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const onYesRejection = useEventCallback((item?: NotificationsList) => {
    if (!item?.id) {
      return;
    }

    const dataConfirm = {
      idNotifica: item?.id || undefined,
    };

    (
      onRejection({
        data: dataConfirm,
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      setItemToModal({ item: undefined, isConfirm: false });
      // ricaricare la tabella
      getNotificationsListData(filterSelected || {}, tableProps.page, tableProps.rowsPerPage);
    });
  });

  const body: Array<NotificationsList> = useMemo(() => {
    return (dataList || []).map((item) => {
      return {
        ...item,
        state: getValueStateNotification(item.stateCode),
        typeContent: getNameTypeContent(item.typeContent),
        typeEvent: getNameTypeEvent(item.typeEvent),
        action: (
          <div className="pmi-d-flex">
            <IconButton
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onDetailPress(item);
              }}
              aria-label="Visualizza elemento"
              aria-haspopup="true"
              style={{
                padding: '4px',
              }}
            >
              <Visibility />
            </IconButton>
          </div>
        ),
      };
    });
  }, [dataList]);

  const onChangePage = useEventCallback((page: number) => {
    getNotificationsListData(filterSelected || {}, page, tableProps.rowsPerPage);

    setTablePropsFilterTable(page, tableProps.rowsPerPage);

    setTableProps({
      page,
      rowsPerPage: tableProps.rowsPerPage,
    });
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    getNotificationsListData(filterSelected || {}, 0, rowsPerPage);

    setTablePropsFilterTable(0, rowsPerPage);

    setTableProps({
      page: 0,
      rowsPerPage,
    });
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Table
          ariaLabel="Lista notifiche"
          page={tableProps.page || 0}
          rowsPerPage={tableProps.rowsPerPage || 10}
          body={body}
          count={(body && body[0]?.totalCount) || 0}
          head={headCells}
          onSort={noop}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
          orders={{}}
          isNotLayoutFixed
          noSort
          hideButtons
          isServer
        />
      </Grid>
      {Boolean(itemToModal.item) ? (
        <DeleteAlertModal
          title={titleModal}
          message={messageModal}
          open={Boolean(itemToModal.item)}
          onClose={() => setItemToModal({ item: undefined, isConfirm: false })}
          onYes={() => {
            if (itemToModal.isConfirm) {
              onYesConfirm(itemToModal.item);
            } else {
              onYesRejection(itemToModal.item);
            }
          }}
          onNo={() => setItemToModal({ item: undefined, isConfirm: false })}
        />
      ) : undefined}
      {pdfModal.open ? (
        <ModalViewerPDF
          open={pdfModal.open}
          data={pdfModal.data}
          name={pdfModal.name}
          title={pdfModal.name || 'Anteprima atto'}
          onClose={() => {
            setModalPrintSuccess((oldState) => ({
              ...oldState,
              open: true,
            }));
            setPdfModal({ data: '', open: false, name: '', item: {} });
          }}
        />
      ) : undefined}
      {modalPrintSuccess.open ? (
        <ModalPrintSuccess
          open={modalPrintSuccess.open}
          onYes={() => setModalPrintSuccess({ open: false })}
          onClose={() => onDetailPress(pdfModal.item)}
        />
      ) : undefined}
    </Grid>
  );
};

TableActComponent.displayName = 'Notifications Research - Table Notifications component';

const TableNotifications = memo(TableActComponent);

export default TableNotifications;
